import { useEffect, useRef, type RefObject } from 'react';
import { keyBelongsToField, menuIsOpen, typingIsOnCanvas } from '../../ui/keys';
import { GuideCard } from '../guides/Card';
import { emptyGuide, guidePosition, guideState, guideVisible, type GuideEvent, type ToolFeedback } from '../toolFeedback';

/** An instructional overlay, never a replacement for the native pointer. */
export function ToolCursorGuide({ canvasRef, feedback }: { canvasRef: RefObject<HTMLCanvasElement | null>; feedback: ToolFeedback }) {
  const popup = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let state = { ...emptyGuide };
    let x = 0;
    let y = 0;
    const render = () => {
      const element = popup.current;
      if (!element) return;
      const visible = guideVisible(state) && !menuIsOpen() && !typingIsOnCanvas()
        && !keyBelongsToField(document.activeElement, 'Control');
      if (element.hidden === visible) {
        element.hidden = !visible;
        // Each time it opens, the picture starts from the beginning of its loop.
        if (visible) element.querySelector('svg')?.setCurrentTime(0);
      }
      if (!visible) return;
      const at = guidePosition(x, y, element.offsetWidth, element.offsetHeight, window.innerWidth, window.innerHeight);
      const position = `translate(${at.x}px, ${at.y}px)`;
      if (element.style.transform !== position) element.style.transform = position;
    };
    const update = (event: GuideEvent) => { state = guideState(state, event); render(); };
    const move = (event: PointerEvent) => {
      x = event.clientX;
      y = event.clientY;
      update({ type: 'move', inside: event.target === canvasRef.current, buttons: event.buttons, option: event.ctrlKey });
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Control') update({ type: 'option', down: event.type === 'keydown' });
      if (event.type === 'keydown' && event.key === 'Escape' && guideVisible(state)) {
        update({ type: 'dismiss' });
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const press = () => update({ type: 'press' });
    const reset = () => update({ type: 'reset' });
    window.addEventListener('pointermove', move, { capture: true, passive: true });
    window.addEventListener('pointerdown', press, true);
    window.addEventListener('pointerup', move, true);
    window.addEventListener('pointercancel', reset, true);
    window.addEventListener('keydown', key, true);
    window.addEventListener('keyup', key, true);
    window.addEventListener('blur', reset);
    window.addEventListener('resize', reset);
    document.addEventListener('visibilitychange', reset);
    document.addEventListener('focusin', render);
    document.documentElement.addEventListener('pointerleave', reset);
    return () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerdown', press, true);
      window.removeEventListener('pointerup', move, true);
      window.removeEventListener('pointercancel', reset, true);
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('keyup', key, true);
      window.removeEventListener('blur', reset);
      window.removeEventListener('resize', reset);
      document.removeEventListener('visibilitychange', reset);
      document.removeEventListener('focusin', render);
      document.documentElement.removeEventListener('pointerleave', reset);
    };
  }, [canvasRef]);

  return <div ref={popup} className="tool-cursor-guide" role="tooltip" hidden>
    <GuideCard key={feedback.key} guide={feedback.key} name={feedback.name} line={feedback.detail} />
  </div>;
}
